import { useState, useCallback, useMemo } from "react";
import { getStepsForProject, STEP } from "../../views/SubscriptionProjectV2/config/projectConfig";

/**
 * Step state machine for the subscription wizard.
 *
 * Why this is a hook (not just useState in SubscriptionFlow):
 *  Several pieces of state cluster together: which step are we on, what
 *  data has been collected so far, can we go back, can we go forward. Putting
 *  them behind a hook with a small API keeps SubscriptionFlow focused on
 *  rendering instead of state plumbing.
 *
 * State held:
 *  - step      : current step ID (one of STEP.*)
 *  - formData  : accumulated form values from completed steps
 *                shape: { company: {...}, account: {...}, payment: {...} }
 *
 * Methods exposed:
 *  - submitStep(data) : merge step's data, advance to next step
 *  - goBack()         : go to previous step (form data preserved)
 *  - reset()          : back to first step (used after success or fatal error)
 *
 * What this hook does NOT do:
 *  - Validate form data (each step does that with its own zod schema)
 *  - Submit to backend (that's useSubmitSubscription)
 *  - Decide demo-mode skip-payment (the new flow charges every signup)
 */
export function useSubscriptionFlow({ id_project, initialData = {} } = {}) {
  const steps = useMemo(() => getStepsForProject(id_project), [id_project]);

  const [step, setStep] = useState(steps[0]);
  const [formData, setFormData] = useState({
    company: initialData.company ?? {},
    account: initialData.account ?? {},
    payment: initialData.payment ?? {},
  });

  const currentIndex = steps.indexOf(step);

  // Step → key in formData. Keep this map close to the hook so changes
  // stay localized.
  const dataKeyForStep = {
    [STEP.COMPANY]: "company",
    [STEP.ACCOUNT]: "account",
    [STEP.PAYMENT]: "payment",
  };

  const submitStep = useCallback(
    (data) => {
      const key = dataKeyForStep[step];
      if (key) {
        setFormData((prev) => ({ ...prev, [key]: data }));
      }
      const next = steps[currentIndex + 1];
      if (next) setStep(next);
      // If no next step, caller (orchestrator) should detect via isLastStep
      // and trigger useSubmitSubscription instead.
    },
    [step, currentIndex, steps]
  );

  const goBack = useCallback(() => {
    const prev = steps[currentIndex - 1];
    if (prev) setStep(prev);
  }, [currentIndex, steps]);

  const reset = useCallback(() => {
    setStep(steps[0]);
  }, [steps]);

  return {
    // Current state
    step,
    steps,
    formData,
    currentIndex,
    isFirstStep: currentIndex === 0,
    isLastStep: currentIndex === steps.length - 1,

    // Actions
    submitStep,
    goBack,
    reset,
  };
}